import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, vShow as _vShow, withDirectives as _withDirectives, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { onBeforeMount, computed, ref } from 'vue'

import ErrorDialog from '@/components/parts/common/ErrorDialog.vue'
import Loading from '@/components/parts/common/Loading.vue'

import { usePoleGroupListStore } from '@/store/app'

import { DIALOG_ERROR_INFO } from '@/mixins/commonFunction'
import { updatePoleGroupInfo } from '@/mixins/communicationFunction'
import { toHexNumber } from '@/utils/math'

import { ErrorDialogInfo, PoleGroupData } from '@/types/Interfaces'

// ==================================
// interface
// ==================================
interface Props {
  dialog: {
    isDialog: boolean;
    item: PoleGroupData;
  }
}

interface Emits {
  (e: 'show-error-dialog', result: boolean, title: string, message: string): void;
  (e: 'dialogClose'): void;
}

// ==================================
// data
// ==================================

export default /*@__PURE__*/_defineComponent({
  __name: 'PoleGroupUpdateDialog',
  props: {
    dialog: {}
  },
  emits: ["show-error-dialog", "dialogClose"],
  setup(__props: any, { emit: __emit }) {

/**
 * PoleGroupUpdateDialog.vue
 * ポールグループ情報の更新ダイアログ
 * 
 * 親コンポーネント
 * @/views/GroupSetting.vue
 */
// ==================================
// import
// ==================================
const poleGroupListStore = usePoleGroupListStore()

const props = __props

// ローディングアイコン表示判定
const isLoading = ref<boolean>(false)

// ポールグループ一覧
const groups = ref<PoleGroupData[]>([])

// 選択したグループID
const selected = ref<unknown | number>()

// 元々のグループID
const originalSelected = ref<number>()

// 選択されたポール情報
const poleInfo = ref({
  poleid: 0,
  polename: ''
})

// エラーダイアログ情報
const errDialog = ref<ErrorDialogInfo>({
  isShow: false,
  title: '',
  message: ''
})

// バリデーションチェック
const rules = ref({
  /**
   * ポールグループ名のチェック
   * @param {Number} value フォームで選択したグループID
   * @returns エラー文字列、成功判定
   */
  checkInValidGroupName: (value: number | null): string | boolean => {
    if (value === void 0 || value === null) {
      // 値が空ではないこと
      return '値を選択してください'
    } else if (value === originalSelected.value) {
      // 古い値ではないこと
      return '新しいグループ名を選択してください'
    }
    return true
  }
})

// ==================================
// computed
// ==================================
// ダイアログの表示判定用
const isDialog = computed(() => {
  return props.dialog.isDialog
})

// ==================================
// hook
// ==================================
onBeforeMount(() => {
  initialize()
})

// ==================================
// method
// ==================================
const emit = __emit

/**
 * 初期処理
 */
const initialize = () => {
  // Piniaストアからポールグループ一覧を取得して初期値を設定する
  groups.value = poleGroupListStore.$state.poleGroupList
  selected.value = props.dialog.item.groupid
  originalSelected.value = props.dialog.item.groupid
  poleInfo.value.poleid = props.dialog.item.poleid
  poleInfo.value.polename = props.dialog.item.polename
}

/**
 * 変更ボタン活性/非活性の判定
 */
const isValidation = (): boolean => {
  return ((selected.value !== void 0 || selected.value !== null) && originalSelected.value !== selected.value)
}

/**
 * ポールのグループ情報更新処理
 */
const onClickUpdate = () => {
  // 更新先のグループ名を取得する
  let groupName = ''
  const targetGroup = groups.value.find(group => group.groupid === selected.value)
  if (targetGroup !== void 0) {
    groupName = targetGroup.groupname
  }
  isLoading.value = true
  // APIにアクセス
  if (typeof selected.value === 'number') {
    let promise = updatePoleGroupInfo(poleInfo.value.poleid, selected.value)
    // 成否に関わらず、ダイアログは閉じる
    promise
      .then(() => {
        isLoading.value = false
        emit('show-error-dialog', true, 'ポールグループ更新成功','ポールグループを更新しました。\nポールグループID: ' + toHexNumber(poleInfo.value.poleid) + '、ポールグループ名: ' + groupName)
      })
      .catch(() => {
        isLoading.value = false
        // エラーメッセージを設定する
        let title = DIALOG_ERROR_INFO.title.updateError
        let message = DIALOG_ERROR_INFO.message.updateErrorPoleGroupInfo
        emit('show-error-dialog', false, title, message)
      })
  }
}

/**
 * ダイアログ閉じる処理
 */
const dialogClose = () => {
  emit('dialogClose')
}

return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_autocomplete = _resolveComponent("v-autocomplete")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_container = _resolveComponent("v-container")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createBlock(_component_v_dialog, {
    modelValue: isDialog.value,
    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((isDialog).value = $event)),
    class: "pole-group-update-dialog",
    width: "500px",
    persistent: ""
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card, null, {
        default: _withCtx(() => [
          _createVNode(_component_v_container, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, {
                class: "pole-group-update-dialog__title",
                nav: "",
                density: "compact"
              }, {
                default: _withCtx(() => _cache[2] || (_cache[2] = [
                  _createTextVNode(" ポールのグループ編集 ")
                ])),
                _: 1
              }),
              _createVNode(_component_v_divider),
              _createVNode(_component_v_card_text, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_form, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_row, null, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_col, { cols: "3" }, {
                            default: _withCtx(() => _cache[3] || (_cache[3] = [
                              _createElementVNode("span", null, "ポールID", -1)
                            ])),
                            _: 1
                          }),
                          _createVNode(_component_v_col, { cols: "9" }, {
                            default: _withCtx(() => [
                              _createElementVNode("span", null, _toDisplayString(_unref(toHexNumber)(poleInfo.value.poleid)), 1)
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_v_row, null, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_col, { cols: "3" }, {
                            default: _withCtx(() => _cache[4] || (_cache[4] = [
                              _createElementVNode("span", null, "ポール名", -1)
                            ])),
                            _: 1
                          }),
                          _createVNode(_component_v_col, { cols: "9" }, {
                            default: _withCtx(() => [
                              _createElementVNode("span", null, _toDisplayString(poleInfo.value.polename), 1)
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_v_row, null, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_col, { cols: "3" }, {
                            default: _withCtx(() => _cache[5] || (_cache[5] = [
                              _createElementVNode("span", null, "グループ名", -1)
                            ])),
                            _: 1
                          }),
                          _createVNode(_component_v_col, { cols: "9" }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_autocomplete, {
                                modelValue: selected.value,
                                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selected).value = $event)),
                                rules: [rules.value.checkInValidGroupName],
                                items: groups.value,
                                "item-title": "groupname",
                                "item-value": "groupid",
                                clearable: ""
                              }, null, 8, ["modelValue", "rules", "items"])
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_divider),
              _createVNode(_component_v_card_actions, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_spacer),
                  _createVNode(_component_v_btn, {
                    disabled: !isValidation(),
                    color: "primary darken-2",
                    class: "pole-group-update-dialog__update-btn",
                    onClick: onClickUpdate
                  }, {
                    default: _withCtx(() => _cache[6] || (_cache[6] = [
                      _createTextVNode(" 変更 ")
                    ])),
                    _: 1
                  }, 8, ["disabled"]),
                  _createVNode(_component_v_btn, {
                    color: "grey darken-2",
                    dark: "",
                    onClick: dialogClose
                  }, {
                    default: _withCtx(() => _cache[7] || (_cache[7] = [
                      _createTextVNode(" キャンセル ")
                    ])),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(ErrorDialog, { "error-dialog": errDialog.value }, null, 8, ["error-dialog"]),
      _withDirectives(_createVNode(Loading, null, null, 512), [
        [_vShow, isLoading.value]
      ])
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})